import React from "react";
import Link from "next/link";
import { cva, type VariantProps } from "class-variance-authority";
import type { LinkData } from "@/types/link";
import type { IconName } from "@/types/icons";
import { Icon } from "../Icon";

const iconButtonVariants = cva(
  "inline-flex items-center justify-center rounded-full aspect-square p-sm transition-all duration-200 cursor-pointer outline-offset-0 focus:outline-2 focus:outline-offset-2",
  {
    variants: {
      variant: {
        primary: "",
        secondary: "border-2",
      },
      color: {
        purple: "outline-purple",
        yellow: "outline-yellow",
        "yellow-purple-hover": "outline-purple-dark",
        dark: "outline-yellow",
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        color: "purple",
        class: "bg-purple text-white hover:bg-yellow hover:text-black",
      },
      {
        variant: "primary",
        color: "yellow",
        class: "bg-yellow text-black hover:bg-white",
      },
      {
        variant: "primary",
        color: "dark",
        class: "bg-black/40 text-yellow hover:bg-black/50 outline-yellow",
      },
      {
        variant: "primary",
        color: "yellow-purple-hover",
        class: "bg-yellow text-black hover:bg-purple hover:text-white",
      },
      {
        variant: "primary",
        color: "dark",
        class: "bg-black/40 text-yellow hover:bg-black/50 outline-yellow",
      },

      {
        variant: "secondary",
        color: "yellow",
        class:
          "bg-transparent border-yellow text-yellow hover:border-white hover:text-white",
      },
    ],
    defaultVariants: {
      variant: "primary",
      color: "purple",
    },
  }
);

export interface IconButtonProps
  extends
    Partial<Omit<LinkData, "label">>,
    VariantProps<typeof iconButtonVariants> {
  /**
   * Icon to display in the button
   */
  icon: IconName;
  /**
   * Optional label for accessibility (falls back to ariaLabel)
   */
  label?: string;
  /**
   * Color variant for background and icon color
   * - purple: purple background with white icon
   * - yellow: yellow background with black icon
   * - yellow-purple-hover: yellow background with black icon, purple background with white icon on hover
   * - dark: black background with yellow icon, darker black background on hover
   * @default "purple"
   */
  color?: "purple" | "yellow" | "yellow-purple-hover" | "dark";
  /**
   * Size of the icon (controls button size)
   * @default 24
   */
  size?: number;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Optional click handler
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  /**
   * Optional disabled state
   */
  disabled?: boolean;
}

/**
 * Circular icon button with link or button functionality
 */
export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  href,
  target,
  rel,
  isActive,
  ariaLabel,
  label,
  variant = "primary",
  color = "purple",
  size = 24,
  className = "",
  onClick,
  disabled,
}) => {
  const content = <Icon name={icon} size={size} decorative />;

  if (href) {
    return (
      <Link
        href={href}
        target={target}
        rel={rel}
        aria-label={ariaLabel || label}
        aria-current={isActive ? "page" : undefined}
        className={iconButtonVariants({ variant, color, className })}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
        role="button"
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel || label}
      className={iconButtonVariants({ variant, color, className })}
      onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
    >
      {content}
    </button>
  );
};
